import { type FC, useState, useEffect, useMemo, useCallback } from "react";
import { Select, Input, Tooltip, Form } from "antd";
import type { FormInstance } from "antd/es/form";
import type { TableProps } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table/interface";
import type { Dayjs } from "dayjs";
import { AppStrings } from "../../../utils/constants/AppStrings";
import KpiCard from "../../CommonComponents/Dashboard/KpiCard/KpiCard";
import {
  BRAND_ICONS_URLS,
  KPI_CARD_BACKGROUND,
} from "../../../utils/Constants";
import { useManualActionsFlags } from "../../../hooks/useFeatureFlag";
import { getColorBasedOnValue } from "../../../utils/colorUtils";
import styles from "./WalletManualActions.module.css";
import DataTable from "../../shared/dashboards/DataTable/DataTable";
import AgentPerformanceView from "../AgentPerformanceView/AgentPerformanceView";
import { NAVIGATION_ROUTES } from "../../../utils/NavigationUtils";
import { getManualActionsStaffManagementHref } from "../manualActionsStaffDeepLink";
import { convertToUserTimezone } from "../../../utils/dateService";
import CustomAutoCompleteControlled from "../../CommonComponents/CustomAutoComplete/CustomAutoCompleteControlled";
import {
  useGetAALWalletTable,
  type WalletAALTableItem,
  type WalletAALTableFilters,
} from "../../../queries/agent-audit-log/wallet/useGetAALWalletTable";
import { toAalSort } from "../manualActionsTableShared";
import {
  useGetAALWalletKPIs,
  type WalletAALKPIsResponse,
} from "../../../queries/agent-audit-log/wallet/useGetAALWalletKPIs";
import { useDownloadAALWalletTable } from "../../../queries/agent-audit-log/wallet/useDownloadAALWalletTable";
import { useDownloadOnTrigger } from "../../../hooks/useDownloadOnTrigger";
import { useGetRoles } from "../../../queries/useRolesApi";
import { useGetStaff } from "../../../queries/useGetStaff";
import UserAvatar from "../../CommonComponents/UserAvatar/UserAvatar";

// CustomAutoCompleteControlled is a JS component — cast to avoid incorrect prop inference
type AutoCompleteOption = { value: string; label: string; id: string };
type CustomAutoCompleteProps = {
  allOptions: AutoCompleteOption[];
  selector: string;
  value: string;
  handleChange: (val: string) => void;
  clearOptions: () => void;
  placeholder?: string;
  onSearchChange?: (val: string) => void;
};
const TypedAutoComplete =
  CustomAutoCompleteControlled as unknown as FC<CustomAutoCompleteProps>;

type WalletTableOnChange = NonNullable<
  TableProps<WalletAALTableItem>["onChange"]
>;

type WalletPagination = {
  current: number;
  pageSize: number;
  total: number;
  onChange: (page: number, pageSize: number) => void;
};

// DataTable is a JS component — cast to a typed FC to avoid incorrect prop inference
type DataTableProps = {
  columns: ColumnsType<WalletAALTableItem>;
  dataSource: WalletAALTableItem[];
  loading: boolean;
  onChange: WalletTableOnChange;
  rowKey: (record: WalletAALTableItem) => string;
  scroll: { x: number; y: number };
  pagination: WalletPagination;
};
const TypedDataTable = DataTable as unknown as FC<DataTableProps>;

type Role = {
  id: string;
  name: string;
  color: string;
  is_system_role: boolean;
  has_all_permissions: boolean;
  in_use: boolean;
  created_at: string;
  updated_at: string;
  actions: string[];
  permissions: string[];
};

type RoleById = Record<string, { name: string; color: string }>;

/** Agent performance table row — API fields not modeled on `WalletAALTableItem`. */
type WalletAalApvRow = {
  agent_email: string;
  rebet_cash_delta?: number;
  rebet_cash_pct_change?: number | null;
  casino_cash_delta?: number;
  casino_cash_pct_change?: number | null;
  p_cash_delta?: number;
  p_cash_pct_change?: number | null;
  agent_role_id?: string;
};

interface WalletManualActionsProps {
  agentPerformanceView?: boolean;
  dates?: [Dayjs, Dayjs] | null;
  showFilters?: boolean;
  downloadTrigger?: number;
}

type PanelFilters = {
  money_type: string | null;
  agent: string;
  role: string | null;
  username: string;
};
const INITIAL_PANEL_FILTERS: PanelFilters = {
  money_type: null,
  agent: "",
  role: null,
  username: "",
};

type SelectOption = { value: string; label: string };

/**
 * Maps Ant Design column sort `field` to backend `sort_by`.
 * Detail table: `created_at` (the "Date&Time" column) → `date`.
 */
function walletSortFieldForApi(field: string): string {
  if (field === "created_at") return "date";
  return field;
}

function formatWalletCurrency(value: number | string, percentage = false) {
  return new Intl.NumberFormat("en-US", {
    style: percentage ? "percent" : "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value ?? 0));
}

function renderWalletKpiDeltaTooltip(delta: number) {
  return (
    <div style={{ padding: "4px 8px", fontFamily: "Poppins-regular" }}>
      <div style={{ marginBottom: "8px" }}>
        <div style={{ fontSize: "12px", textTransform: "uppercase" }}>
          {AppStrings.DELTA_SYMBOL} Total
        </div>
        <div
          style={{
            color: delta >= 0 ? "#71BF8A" : "#E74C3C",
            fontSize: "16px",
            fontWeight: 600,
          }}
        >
          {formatWalletCurrency(delta)}
        </div>
      </div>
    </div>
  );
}

function renderPercentSpan(percent: number) {
  const sign = percent >= 0 ? "+" : "";
  return (
    <span
      style={{
        color: getColorBasedOnValue(percent),
        fontSize: "11px",
        marginLeft: "4px",
      }}
    >
      {sign}
      {percent.toFixed(1)}%
    </span>
  );
}

function buildWalletTableColumns(
  moneyTypeLabelByValue: Record<string, string>,
  roleById: RoleById,
): ColumnsType<WalletAALTableItem> {
  return [
    {
      title: AppStrings.DATE_AND_TIME,
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      showSorterTooltip: false,
      width: 180,
      render: (text: string) => <span>{convertToUserTimezone(text)}</span>,
    },
    {
      title: AppStrings.MONEY_TYPE,
      dataIndex: "money_type",
      key: "money_type",
      width: 150,
      render: (value: string) => moneyTypeLabelByValue[value] ?? value,
    },
    {
      title: AppStrings.AMOUNT,
      dataIndex: "amount",
      key: "amount",
      sorter: true,
      showSorterTooltip: false,
      width: 180,
      render: (text: string) => {
        const num = parseFloat(text) || 0;
        const isPositive = num >= 0;
        return (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              color: isPositive ? "#71BF8A" : "#E74C3C",
            }}
          >
            <img
              src={
                process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
                BRAND_ICONS_URLS.primaryCurrencySymbol
              }
              alt="currency"
              style={{ width: "14px", height: "14px" }}
            />
            {isPositive ? "+" : ""}
            {formatWalletCurrency(num)}
          </span>
        );
      },
    },
    {
      title: AppStrings.ROLE,
      dataIndex: "agent_role_id",
      key: "agent_role_id",
      width: 150,
      render: (value: string) => {
        const role = roleById[value];
        if (!role) return <span>{value || "—"}</span>;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: role.color,
                flexShrink: 0,
                display: "inline-block",
              }}
            />
            <span>{role.name}</span>
          </div>
        );
      },
    },
    {
      title: AppStrings.PAM_AGENT,
      dataIndex: "agent_email",
      key: "agent_email",
      showSorterTooltip: false,
      width: 180,
      render: (text: string, record) => (
        <a
          href={getManualActionsStaffManagementHref(record.agent_id)}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.walletManualActionsHoverable}
        >
          {text}
        </a>
      ),
    },
    {
      title: AppStrings.USERNAME,
      dataIndex: "target_username",
      key: "target_username",
      width: 150,
      render: (text: string, record) => (
        <a
          href={`${NAVIGATION_ROUTES.USER_MANAGER}?userId=${record.target_user_id}`}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.walletManualActionsHoverable}
        >
          <UserAvatar />
          {text}
        </a>
      ),
    },
    {
      title: AppStrings.REASON,
      dataIndex: "reason",
      key: "reason",
      width: 300,
    },
  ];
}

function buildAgentPerformanceColumns(
  roleById: RoleById,
  showCasinoKpi: boolean,
  showPikkemKpi: boolean,
): ColumnsType<WalletAalApvRow> {
  const currencyIconSrc =
    process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
    BRAND_ICONS_URLS.primaryCurrencySymbol;

  return [
    {
      title: AppStrings.PAM_AGENT,
      dataIndex: "agent_email",
      key: "agent_email",
      width: 200,
      render: (text: string) => (
        <a
          href={getManualActionsStaffManagementHref()}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.walletManualActionsHoverable}
        >
          {text}
        </a>
      ),
    },
    {
      title: (
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
        >
          {`${AppStrings.DELTA_SYMBOL} ${AppStrings.PRIMARY_CURRENCY}`}
          <Tooltip title={AppStrings.REBET_CASH_TOOLTIP}>
            <InfoCircleOutlined
              style={{
                color: "var(--grey-400)",
                fontSize: "12px",
                cursor: "help",
              }}
            />
          </Tooltip>
        </span>
      ),
      dataIndex: "rebet_cash_delta",
      key: "rebet_cash_delta",
      width: 180,
      sorter: true,
      render: (text: number | undefined, record: WalletAalApvRow) => (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <img
            src={currencyIconSrc}
            alt="currency"
            style={{ width: "14px", height: "14px" }}
          />
          {formatWalletCurrency(text ?? 0)}
          {record.rebet_cash_pct_change != null &&
            renderPercentSpan(record.rebet_cash_pct_change)}
        </span>
      ),
    },
    ...(showCasinoKpi
      ? [
          {
            title: (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {`${AppStrings.DELTA_SYMBOL} ${AppStrings.TOTAL_CASINO_CASH}`}
                <Tooltip title={AppStrings.CASINO_CASH_TOOLTIP}>
                  <InfoCircleOutlined
                    style={{
                      color: "var(--grey-400)",
                      fontSize: "12px",
                      cursor: "help",
                    }}
                  />
                </Tooltip>
              </span>
            ),
            dataIndex: "casino_cash_delta",
            key: "casino_cash_delta",
            width: 180,
            sorter: true,
            render: (text: number | undefined, record: WalletAalApvRow) => (
              <span
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
              >
                <img
                  src={currencyIconSrc}
                  alt="currency"
                  style={{ width: "14px", height: "14px" }}
                />
                {formatWalletCurrency(text ?? 0)}
                {record.casino_cash_pct_change != null &&
                  renderPercentSpan(record.casino_cash_pct_change)}
              </span>
            ),
          } as const,
        ]
      : []),
    ...(showPikkemKpi
      ? [
          {
            title: `${AppStrings.DELTA_SYMBOL} ${AppStrings.PIKKEM_CASH}`,
            dataIndex: "p_cash_delta",
            key: "p_cash_delta",
            width: 180,
            sorter: true,
            render: (text: number | undefined, record: WalletAalApvRow) => (
              <span
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
              >
                <img
                  src={currencyIconSrc}
                  alt="currency"
                  style={{ width: "14px", height: "14px" }}
                />
                {formatWalletCurrency(text ?? 0)}
                {record.p_cash_pct_change != null &&
                  renderPercentSpan(record.p_cash_pct_change)}
              </span>
            ),
          } as const,
        ]
      : []),
    {
      title: AppStrings.ROLE,
      dataIndex: "agent_role_id",
      key: "agent_role_id",
      width: 170,
      render: (value: string | undefined) => {
        const role = value ? roleById[value] : undefined;
        if (!role) return <span>{value || "—"}</span>;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: role.color,
                flexShrink: 0,
                display: "inline-block",
              }}
            />
            <span>{role.name}</span>
          </div>
        );
      },
    },
  ];
}

type WalletManualActionsFiltersSectionProps = {
  visible: boolean;
  form: FormInstance;
  moneyTypeOptions: SelectOption[];
  autoCompleteKey: number;
  agentOptions: AutoCompleteOption[];
  watchedAgent: string;
  roleOptions: SelectOption[];
  isDirty: boolean;
  onClearFilters: () => void;
  onApply: () => void;
  onAgentChange: (agentId: string) => void;
  onAgentClear: () => void;
};

function WalletManualActionsFiltersSection({
  visible,
  form,
  moneyTypeOptions,
  autoCompleteKey,
  agentOptions,
  watchedAgent,
  roleOptions,
  isDirty,
  onClearFilters,
  onApply,
  onAgentChange,
  onAgentClear,
}: WalletManualActionsFiltersSectionProps) {
  const display = visible ? "flex" : "none";
  return (
    <>
      <Form form={form}>
        <div
          className={styles.walletManualActionsSelectors}
          style={{ display }}
        >
          <div className={styles.walletManualActionsSelector}>
            <span>{AppStrings.MONEY_TYPE}</span>
            <Form.Item name="money_type" noStyle>
              <Select
                allowClear
                placeholder={AppStrings.NOT_SELECTED}
                options={moneyTypeOptions}
              />
            </Form.Item>
          </div>
          <div className={styles.walletManualActionsSelector}>
            <span>{AppStrings.PAM_AGENT}</span>
            {/* CustomAutoCompleteControlled uses handleChange not onChange;
                manage its form value manually via form.setFieldValue */}
            <TypedAutoComplete
              key={autoCompleteKey}
              allOptions={agentOptions}
              selector="id"
              value={watchedAgent}
              handleChange={onAgentChange}
              clearOptions={onAgentClear}
              placeholder={AppStrings.NOT_SELECTED}
            />
          </div>
          <div className={styles.walletManualActionsSelector}>
            <span>{AppStrings.ROLE}</span>
            <Form.Item name="role" noStyle>
              <Select
                allowClear
                placeholder={AppStrings.NOT_SELECTED}
                options={roleOptions}
              />
            </Form.Item>
          </div>
          <div className={styles.walletManualActionsSelector}>
            <span>{AppStrings.USERNAME}</span>
            <Form.Item name="username" noStyle>
              <Input
                placeholder="Text"
                className={styles.walletManualActionsFilterInput}
              />
            </Form.Item>
          </div>
        </div>
      </Form>
      <div
        className={styles.walletManualActionsSelectorActionButtons}
        style={{ display }}
      >
        <button type="button" className={styles.clear} onClick={onClearFilters}>
          {AppStrings.CLEAR_FILTERS}
        </button>
        <button
          type="button"
          className={isDirty ? `${styles.apply} ${styles.dirty}` : styles.apply}
          onClick={onApply}
        >
          {AppStrings.APPLY}
        </button>
      </div>
    </>
  );
}

type WalletManualActionsKpiSectionProps = {
  visible: boolean;
  showCasinoKpi: boolean;
  showPikkemKpi: boolean;
  isKpiLoading: boolean;
  totalRebetCash: number;
  totalCasinoCash: number;
  totalPikkemCash: number;
  kpiData: WalletAALKPIsResponse;
};

function WalletManualActionsKpiSection({
  visible,
  showCasinoKpi,
  showPikkemKpi,
  isKpiLoading,
  totalRebetCash,
  totalCasinoCash,
  totalPikkemCash,
  kpiData,
}: WalletManualActionsKpiSectionProps) {
  return (
    <div
      className={styles.walletManualActionsKpiCards}
      style={{ display: visible ? "flex" : "none" }}
    >
      <div className={styles.kpiRow}>
        <div
          style={{
            flex: 1,
            maxWidth: "33.33%",
            position: "relative",
          }}
        >
          <Tooltip title={AppStrings.REBET_CASH_TOOLTIP}>
            <InfoCircleOutlined
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                color: "var(--grey-400)",
                fontSize: 14,
                cursor: "help",
                zIndex: 1,
              }}
            />
          </Tooltip>
          <Tooltip
            title={renderWalletKpiDeltaTooltip(totalRebetCash)}
            placement="top"
            color="var(--COLOR-SPIN_GRAD_COLOR_1 )"
          >
            <div>
              <KpiCard
                title={`${AppStrings.DELTA_SYMBOL} ${AppStrings.PRIMARY_CURRENCY}`}
                value={totalRebetCash}
                hugeValue={false}
                percentage={kpiData?.rebet_cash_pct_change}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isKpiLoading}
                maximumFractionDigits={2}
              />
            </div>
          </Tooltip>
        </div>
        {showCasinoKpi && (
          <div
            style={{
              flex: 1,
              maxWidth: "33.33%",
              position: "relative",
            }}
          >
            <Tooltip title={AppStrings.CASINO_CASH_TOOLTIP}>
              <InfoCircleOutlined
                style={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  color: "var(--grey-400)",
                  fontSize: 14,
                  cursor: "help",
                  zIndex: 1,
                }}
              />
            </Tooltip>
            <Tooltip
              title={renderWalletKpiDeltaTooltip(totalCasinoCash)}
              placement="top"
              color="var(--COLOR-SPIN_GRAD_COLOR_1 )"
            >
              <div>
                <KpiCard
                  title={`${AppStrings.DELTA_SYMBOL} ${AppStrings.TOTAL_CASINO_CASH}`}
                  value={totalCasinoCash}
                  hugeValue={false}
                  percentage={kpiData?.casino_cash_pct_change}
                  backgroundColor={KPI_CARD_BACKGROUND.DARK}
                  isApiRunning={isKpiLoading}
                  maximumFractionDigits={2}
                />
              </div>
            </Tooltip>
          </div>
        )}
        {showPikkemKpi && (
          <Tooltip
            title={renderWalletKpiDeltaTooltip(totalPikkemCash)}
            placement="top"
            color="var(--COLOR-SPIN_GRAD_COLOR_1 )"
          >
            <div style={{ flex: 1, maxWidth: "33.33%" }}>
              <KpiCard
                title={`${AppStrings.DELTA_SYMBOL} ${AppStrings.PIKKEM_CASH}`}
                value={totalPikkemCash}
                hugeValue={false}
                percentage={kpiData?.p_cash_pct_change}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isKpiLoading}
                maximumFractionDigits={2}
              />
            </div>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

type WalletManualActionsResultsTableProps = {
  agentPerformanceView: boolean;
  agentPerformanceColumns: ColumnsType<WalletAalApvRow>;
  walletColumns: ColumnsType<WalletAALTableItem>;
  tableItems: WalletAALTableItem[];
  isTableLoading: boolean;
  isTableFetching: boolean;
  onTableChange: WalletTableOnChange;
  pagination: WalletPagination;
  selectedRole: string[];
  onSelectedRoleChange: (roles: string[]) => void;
  roleOptions: SelectOption[];
};

function WalletManualActionsResultsTable({
  agentPerformanceView,
  agentPerformanceColumns,
  walletColumns,
  tableItems,
  isTableLoading,
  isTableFetching,
  onTableChange,
  pagination,
  selectedRole,
  onSelectedRoleChange,
  roleOptions,
}: WalletManualActionsResultsTableProps) {
  if (agentPerformanceView) {
    return (
      <AgentPerformanceView
        columns={agentPerformanceColumns}
        handleTableChange={onTableChange}
        filteredData={tableItems}
        roleOptions={roleOptions}
        selectedRoles={selectedRole}
        onRoleChange={onSelectedRoleChange}
        pagination={pagination}
      />
    );
  }
  return (
    <TypedDataTable
      columns={walletColumns}
      dataSource={tableItems}
      loading={isTableLoading || isTableFetching}
      onChange={onTableChange}
      rowKey={(record) => record.event_id}
      scroll={{ x: 1800, y: 500 }}
      pagination={pagination}
    />
  );
}

function WalletManualActions({
  agentPerformanceView = false,
  dates,
  showFilters,
  downloadTrigger = 0,
}: WalletManualActionsProps) {
  const allMoneyTypeOptions = [
    { value: "p_cash", label: AppStrings.PIKKEM_CASH },
    { value: "casino_cash", label: AppStrings.CASINO_CASH },
    { value: "rebet_cash", label: AppStrings.PRIMARY_CURRENCY },
    { value: "rebet_coin", label: AppStrings.SECONDARY_CURRENCY },
  ];

  const moneyTypeLabelByValue = Object.fromEntries(
    allMoneyTypeOptions.map((o) => [o.value, o.label]),
  );

  // Filter form — values here are drafts until the user clicks Apply
  const [form] = Form.useForm();
  const watchedAgent = Form.useWatch("agent", form) ?? "";
  const watchedMoneyType = Form.useWatch("money_type", form);
  const watchedRole = Form.useWatch("role", form);
  const watchedUsername = Form.useWatch("username", form);
  // Bump this to force-remount the autocomplete when clearing filters
  const [autoCompleteKey, setAutoCompleteKey] = useState(0);
  // Form.useWatch can't see agent changes (no Form.Item), so we track it separately
  const [isAgentDirty, setIsAgentDirty] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<PanelFilters>(
    INITIAL_PANEL_FILTERS,
  );

  // Check if the form values changed compared to the last applied filters
  const isDirty =
    (watchedMoneyType ?? null) !== appliedFilters.money_type ||
    isAgentDirty ||
    (watchedRole ?? null) !== appliedFilters.role ||
    (watchedUsername ?? "") !== appliedFilters.username;
  // APV role — changes here refetch the table right away (no Apply needed)
  const [selectedRole, setSelectedRole] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(100);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"ascend" | "descend" | null>(null);

  // Feature flags — hide KPIs for disabled products
  const { showCasinoKpi, showPikkemKpi } = useManualActionsFlags();

  const moneyTypeOptions = useMemo(
    () =>
      allMoneyTypeOptions.filter((o) => {
        if (o.value === "p_cash" && !showPikkemKpi) return false;
        if (o.value === "casino_cash" && !showCasinoKpi) return false;
        return true;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showPikkemKpi, showCasinoKpi],
  );

  const start_date = dates?.[0]?.utc().format("YYYY-MM-DD");
  const end_date = dates?.[1]?.utc().format("YYYY-MM-DD");
  const listView = agentPerformanceView ? "agents" : "events";

  const tableFilters: WalletAALTableFilters = {
    view: listView,
    start_date: start_date ?? "",
    end_date: end_date ?? "",
    page: currentPage,
    size: pageSize,
    sort: toAalSort(walletSortFieldForApi(sortField ?? ""), sortOrder),
    // In APV mode use the role dropdown value; in normal mode use the applied filter
    ...(agentPerformanceView
      ? selectedRole.length > 0 && { agent_role_id: selectedRole[0] }
      : appliedFilters.role && { agent_role_id: appliedFilters.role }),
    // These filters only apply in normal mode (not APV) after clicking Apply
    ...(!agentPerformanceView &&
      appliedFilters.money_type && { money_type: appliedFilters.money_type }),
    ...(!agentPerformanceView &&
      appliedFilters.agent && { agent_id: appliedFilters.agent }),
    ...(!agentPerformanceView &&
      appliedFilters.username && { username: appliedFilters.username }),
  };

  // KPIs only care about the date range
  const kpiFilters = {
    start_date: start_date ?? "",
    end_date: end_date ?? "",
  };

  const {
    items: tableItems,
    total: totalResults,
    isLoading: isTableLoading,
    isFetching: isTableFetching,
  } = useGetAALWalletTable(tableFilters);
  const { data: kpiData, isLoading: isKpiLoading } =
    useGetAALWalletKPIs(kpiFilters);
  const { data: roles } = useGetRoles() as { data: Role[] | undefined };
  const { data: staffData } = useGetStaff({
    page_size: 100,
  });
  const agentOptions = (staffData?.items ?? []).map((s) => ({
    value: s.email,
    label: s.email,
    id: s.id,
  }));
  const roleOptions = (roles ?? []).map((r) => ({
    value: r.id,
    label: r.name,
  }));

  // Quick lookup: role ID - { name, color } for the Role column
  const roleById: RoleById = Object.fromEntries(
    (roles ?? []).map((r) => [
      r.id,
      { name: r.name, color: r.color ?? "#6b7280" },
    ]),
  );

  // Go back to page 1 when the APV role changes
  useEffect(() => {
    if (agentPerformanceView) {
      setCurrentPage(1);
    }
  }, [selectedRole, agentPerformanceView]);

  // Clean up when toggling between normal view and APV
  useEffect(() => {
    if (!agentPerformanceView) {
      setSelectedRole([]);
    }
    setSortField(null);
    setSortOrder(null);
    setCurrentPage(1);
  }, [agentPerformanceView]);

  // Download — fires when the parent bumps downloadTrigger
  const { start: downloadTable } = useDownloadAALWalletTable(listView);
  useDownloadOnTrigger(downloadTrigger, downloadTable, () => ({
    start_date: start_date ?? "",
    end_date: end_date ?? "",
    ...(agentPerformanceView
      ? selectedRole.length > 0
        ? { agent_role_id: selectedRole[0] }
        : {}
      : {
          ...(appliedFilters.money_type && {
            money_type: appliedFilters.money_type,
          }),
          ...(appliedFilters.agent && { agent_id: appliedFilters.agent }),
          ...(appliedFilters.role && { agent_role_id: appliedFilters.role }),
          ...(appliedFilters.username && {
            username: appliedFilters.username,
          }),
        }),
  }));

  const totalRebetCash = parseFloat(kpiData?.rebet_cash_delta ?? "0");
  const totalCasinoCash = parseFloat(kpiData?.casino_cash_delta ?? "0");
  const totalPikkemCash = parseFloat(kpiData?.p_cash_delta ?? "0");

  const handleClearFilters = useCallback(() => {
    form.resetFields();
    form.setFieldsValue({ agent: "" });
    // Remount the autocomplete so it fully resets
    setAutoCompleteKey((k) => k + 1);
    setIsAgentDirty(false);
    setAppliedFilters(INITIAL_PANEL_FILTERS);
    setCurrentPage(1);
  }, [form]);

  const handleApply = useCallback(() => {
    const values = form.getFieldsValue();
    setAppliedFilters({
      money_type: values.money_type ?? null,
      // AutoComplete isn't a Form.Item, so we read its value manually
      agent: form.getFieldValue("agent") ?? "",
      role: values.role ?? null,
      username: values.username ?? "",
    });
    setIsAgentDirty(false);
    setCurrentPage(1);
  }, [form]);

  const handleTableChange: WalletTableOnChange = useCallback(
    (_pagination, _filters, sorter) => {
      const s = Array.isArray(sorter) ? sorter[0] : sorter;
      if (s.field) {
        setSortField(s.field as string);
        setSortOrder(s.order ?? null);
      } else {
        setSortField(null);
        setSortOrder(null);
      }
    },
    [],
  );

  const handleAgentChange = useCallback(
    (agentId: string) => {
      form.setFieldValue("agent", agentId);
      setIsAgentDirty(true);
    },
    [form],
  );

  const handleAgentClear = useCallback(() => {
    form.setFieldValue("agent", "");
  }, [form]);

  const handlePaginationChange = useCallback(
    (page: number, size: number) => {
      setCurrentPage(page);
      setPageSize(size);
    },
    [],
  );

  const walletColumns = buildWalletTableColumns(moneyTypeLabelByValue, roleById);
  const agentPerformanceColumns = buildAgentPerformanceColumns(
    roleById,
    showCasinoKpi,
    showPikkemKpi,
  );

  const filtersVisible = Boolean(showFilters && !agentPerformanceView);
  const kpiVisible = !agentPerformanceView;

  const pagination: WalletPagination = useMemo(
    () => ({
      current: currentPage,
      pageSize,
      total: totalResults,
      onChange: handlePaginationChange,
    }),
    [currentPage, pageSize, totalResults, handlePaginationChange],
  );

  return (
    <div className={styles.walletManualActionsContainer}>
      <WalletManualActionsFiltersSection
        visible={filtersVisible}
        form={form}
        moneyTypeOptions={moneyTypeOptions}
        autoCompleteKey={autoCompleteKey}
        agentOptions={agentOptions}
        watchedAgent={watchedAgent}
        roleOptions={roleOptions}
        isDirty={isDirty}
        onClearFilters={handleClearFilters}
        onApply={handleApply}
        onAgentChange={handleAgentChange}
        onAgentClear={handleAgentClear}
      />
      <WalletManualActionsKpiSection
        visible={kpiVisible}
        showCasinoKpi={showCasinoKpi}
        showPikkemKpi={showPikkemKpi}
        isKpiLoading={isKpiLoading}
        totalRebetCash={totalRebetCash}
        totalCasinoCash={totalCasinoCash}
        totalPikkemCash={totalPikkemCash}
        kpiData={kpiData ?? null}
      />
      <WalletManualActionsResultsTable
        agentPerformanceView={agentPerformanceView}
        agentPerformanceColumns={agentPerformanceColumns}
        walletColumns={walletColumns}
        tableItems={tableItems}
        isTableLoading={isTableLoading}
        isTableFetching={isTableFetching}
        onTableChange={handleTableChange}
        pagination={pagination}
        selectedRole={selectedRole}
        onSelectedRoleChange={setSelectedRole}
        roleOptions={roleOptions}
      />
    </div>
  );
}

export default WalletManualActions;
